import { Box } from "@mui/material";
import DetailDataGrid from "../../../components/AppDataGrid/DetailDataGrid";
import { format } from "date-fns";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMessage } from "../../../../base/MessageProvider";
import {
  deleteAuditions,
  getPastAuditionList,
} from "../../../actions/auditions";
import { StorageConstants } from "../../../storage/storageConstants";
import useDebounce from "../../../../base/useDebounce";
import { getGridStringOperators } from "@mui/x-data-grid";
import {
  deepCopy,
  hideLoadingOverlay,
  showLoadingOverlay,
} from "../../../../utils";

const PastAuditions = forwardRef((props, ref) => {
  useImperativeHandle(ref, () => ({
    refreshData() {
      fetchData();
    },
    delete() {
      onPressDeleteAll();
    },
  }));

  const { visible } = props;

  const storedPage = localStorage.getItem(StorageConstants.PAST_AUDITION_PAGE);
  const storedPageSize = localStorage.getItem(
    StorageConstants.PAST_AUDITION_PER_PAGE
  );

  const dispatch = useDispatch();
  const alert = useMessage();

  const selectedAuditionTab =
    useSelector((state) => state.auditionReducer.selectedAuditionTab) ?? 0;
  const loading = useSelector((state) => state.auditionReducer?.loading);
  const selectedLocation =
    useSelector((state) => state.auditionReducer.selectedLocation) ?? "ALL";
  const pastAuditionsList =
    useSelector((state) => state.auditionReducer?.pastAuditionsList) ?? [];
  const pastAuditionsListMetaData = useSelector(
    (state) => state.auditionReducer?.pastAuditionsListMetaData
  );

  const [searchTerm, setSearchTerm] = useState("");
  const debouncedSearchTerm = useDebounce(searchTerm, 800);

  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowsState, setRowsState] = useState({
    page: storedPage ? +storedPage : 0,
    pageSize: storedPageSize ? +storedPageSize : 10,
  });

  const onSelectionModelChange = (model, details) => setSelectedRows(model);

  const columns = [
    {
      field: "userName",
      headerName: "Name",
      valueGetter: (params) => params.value || "-",
      minWidth: 150,
      filterable: false,
    },
    {
      field: "projectName",
      headerName: "Project",
      valueGetter: (params) => params.value || "-",
      minWidth: 150,
      filterable: false,
    },
    {
      field: "addedAt",
      headerName: "Date",
      minWidth: 185,
      filterable: false,
      valueGetter: (params) =>
        `${format(new Date(params.value), "EEEE")} at ${format(
          new Date(params.value),
          "h:mm aaa"
        )}`,
    },
    {
      field: "minAge",
      headerName: "Age",
      valueGetter: (params) => `${params.value}-${params.row?.maxAge}`,
      width: 105,
      filterable: false,
    },
    {
      field: "projectCategory",
      headerName: "Project Category",
      valueGetter: (params) => params.row.projectCategory?.name || "-",
      minWidth: 195,
      filterOperators: getGridStringOperators().filter(
        (a) => a.value === "contains"
      ),
    },
    {
      field: "profileCategoryName",
      headerName: "Profile Category",
      valueGetter: (params) =>
        params.value ? `${params.value}, ${params.row?.roleTypeName}` : "-",
      minWidth: 195,
      filterOperators: getGridStringOperators().filter(
        (a) => a.value === "contains"
      ),
    },
    {
      field: "auditionLocation",
      headerName: "Location",
      valueGetter: (params) => params.value || "-",
      minWidth: 160,
    },
    {
      field: "jobCode",
      headerName: "Audition",
      width: 100,
      filterOperators: getGridStringOperators().filter(
        (a) => a.value === "contains"
      ),
    },
  ];

  const onFilterModelChange = (model) => {
    const filterModel = deepCopy(model);
    console.log(filterModel.items);

    if (filterModel.items.length && !filterModel.items[0].value) {
      setSearchTerm("");
      return;
    }
    setSearchTerm(filterModel.items[0]?.value);
  };

  const onPaginationModelChange = (model) => {
    setRowsState(model);
    localStorage.setItem(
      StorageConstants.PAST_AUDITION_PAGE,
      JSON.stringify(model.page)
    );
    localStorage.setItem(
      StorageConstants.PAST_AUDITION_PER_PAGE,
      JSON.stringify(model.pageSize)
    );
  };

  const handleDelete = async () => {
    showLoadingOverlay();
    dispatch(
      deleteAuditions(
        { includesJobId: selectedRows?.join(",") },
        (res) => {
          fetchData();
          setSelectedRows([]);
          alert.showSuccess(res?.message ?? "Successfully deleted");
        },
        (error) => {
          alert.showError(error?.message ?? "Something went wrong");
        }
      )
    );
    hideLoadingOverlay();
  };

  const onPressDeleteAll = () => {
    if (!selectedRows?.length) {
      alert.showError("Please select atleast one row!!");
      return;
    }
    alert.showConfirmation({
      message: "Are you sure you want to delete?",
      button1Title: "Cancel",
      button2Title: "Delete",
      onPressBtn1: () => {},
      onPressBtn2: handleDelete,
    });
  };

  useEffect(() => {
    if (pastAuditionsList) {
      setRows(
        pastAuditionsList.map((item) => ({
          ...item,
          id: item.jobId,
          userName: item?.user
            ? `${item?.user.firstName} ${item?.user?.lastName ?? ""}`
            : null,
          projectName: item?.project?.name,
          profileCategoryName: item?.profileCategory?.name,
          roleTypeName: item?.roleType?.name,
        }))
      );
    }
  }, [pastAuditionsList]);

  const fetchData = async () => {
    dispatch(
      getPastAuditionList({
        page: +rowsState.page + 1,
        perPage: rowsState.pageSize,
        searchedKeyword:
          debouncedSearchTerm === "" ? undefined : debouncedSearchTerm?.trim(),
        location: selectedLocation === "ALL" ? undefined : selectedLocation,
      })
    );
  };

  useEffect(() => {
    if (visible) {
      if (debouncedSearchTerm) {
        fetchData();
      } else {
        setRowsState({
          page: storedPage ? +storedPage : 0,
          pageSize: storedPageSize ? +storedPageSize : 10,
        });
        fetchData();
      }
    }
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (visible) {
      fetchData();
    }
  }, [visible, rowsState, selectedLocation]);

  if (!visible) return <></>;

  return (
    <Box>
      <DetailDataGrid
        rows={rows}
        columns={columns}
        paginationModel={rowsState}
        searchTerm={searchTerm}
        onSearchTermChange={setSearchTerm}
        onPaginationModelChange={onPaginationModelChange}
        onFilterModelChange={onFilterModelChange}
        rowCount={+pastAuditionsListMetaData?.total_pagination_job_count || 0}
        loading={loading}
        onPressDelete={onPressDeleteAll}
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={onSelectionModelChange}
      />
    </Box>
  );
});

export default PastAuditions;
